import { Card, CardContent, CardHeader, Typography, Tooltip, makeStyles } from "@material-ui/core";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsToolTip } from "recharts";
import DropdownCustom from "../../../../components/DropdownCustom";
import CustomTooltip from "./CustomTooltip";
import config from "../../../../data/application.properties";
import {useTranslation} from "react-i18next";
import "./css/licenseCardComponent.css";
import { LICENSE_TEXT } from "../../../../data/constants";

const axiosInstance = require("../../../../axios/axios").default;

const LicenseCardComponent = ({
  title,
  tooltipText,
  usedLabel,
  unusedLabel,
  totalLabel,
  dropdownOptions
}) => {

  const useStyles = makeStyles((theme) => ({
    customTooltip: {
      backgroundColor: "black",
      color: "white",
      fontSize: "12px",
    },
    customArrow:{
      color: "black"
    }
  }));

  const classes = useStyles();
  const  {t} = useTranslation();

  const dropDonwList = dropdownOptions || [];
  
  const [licenseData, setLicenseData] = useState();
  const [totalValue, setTotalValue] = useState();
  const [data, setData] = useState([]);
  const [licenseFilterVal, setLicenseFilterVal] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const licenseFilterDefaultVal = dropdownOptions?.[0] || { label: t("license.dropdownOptions.deviceGuardian"), value: t("license.dropdownOptions.deviceGuardianValue") };

  useEffect(() => {
    getLicenseDeviceData();
  }, []);

  const getLicenseDeviceData = async () => {
    try {
      const response = await axiosInstance.post(config.FETCH_LICENSE_SUMMARY, {});
      let res = response.data[LICENSE_TEXT.DETAILS];
      const prepLicenseData = {};
      
      // Mobile-device licenses data prep
      const mobile_device_licenses_data = res[LICENSE_TEXT.DEVICES_LICENSE];
      prepLicenseData[LICENSE_TEXT.DEVICE_GUARDIAN] = [
        { name: LICENSE_TEXT.USED, value: mobile_device_licenses_data[LICENSE_TEXT.CONSUMED], color: '#11c293', total: mobile_device_licenses_data[LICENSE_TEXT.TOTAL] },
        { name: LICENSE_TEXT.UNUSED, value: mobile_device_licenses_data[LICENSE_TEXT.AVAILABLE], color: '#8A2BE2', total: mobile_device_licenses_data[LICENSE_TEXT.TOTAL] },
      ];
  

      // Accessories licenses data prep
      const accessories_licenses_data = res[LICENSE_TEXT.ACCESORIES_LICENSE];
      prepLicenseData[LICENSE_TEXT.ACCESSORIES] = [
        { name: LICENSE_TEXT.USED, value: accessories_licenses_data[LICENSE_TEXT.CONSUMED], color: '#11c293', total: accessories_licenses_data[LICENSE_TEXT.TOTAL] },
        { name: LICENSE_TEXT.UNUSED, value: accessories_licenses_data[LICENSE_TEXT.AVAILABLE], color: '#8A2BE2', total: accessories_licenses_data[LICENSE_TEXT.TOTAL] },
      ];
      setLicenseData(prepLicenseData);
      setLicenseFilterVal(licenseFilterDefaultVal);
      const finalArray = prepLicenseData[licenseFilterDefaultVal.value];
      setTotalValue(finalArray[0]?.total);
      setData(finalArray);
      setIsLoading(false);
    } catch (error) {
      console.log("getLicenseDeviceData Error: ", error);
      setIsLoading(false);
    }
  };

  const handleDropdownChange = (event) => {
    const selectedOptionValue = event?.target?.value;
    if (!licenseData) {
      console.error("License data is not available.");
      return;
    }
    const finalArray = licenseData[selectedOptionValue];
    setTotalValue(finalArray[0]?.total);
    setData(finalArray);
    const selectedOption = dropDonwList.filter(ele => ele.value === selectedOptionValue)[0];
    setLicenseFilterVal(selectedOption);
  };

  return (
    <Card data-testid="license-card" className="license-card">
      {/* Title */}
      <CardHeader
        title={
          <div className="card-header-container" data-testid="card-header">
            <Typography variant="h6" data-testid="card-title">{title}</Typography>
            <Tooltip
              title={tooltipText}
              arrow
              placement="top"
              classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }}
            >
              <InfoOutlinedIcon
                className="info-icon"
                data-testid="info-icon"
              />
            </Tooltip>
          </div>
        }
        action={
          <DropdownCustom
            selectOpts={dropDonwList}
            onChange={handleDropdownChange}
            isLoading={isLoading}
            selectVal={licenseFilterVal || licenseFilterDefaultVal}
            data-testid="dropdown"
          />
        }
      />
      <CardContent>
        <div
          className="card-content-container"
          data-testid="card-content"
        >
          {/* Stats */}
          <div data-testid="stats">
            {data.map((entry) => (
              <div key={entry.name} className="stat-item">
                <div className="color-circle" style={{ backgroundColor: entry.color }}></div>
                <Typography className="stat-text" style={{ color: entry.color }}>
                  <span className="stat-text">
                    {`${entry.name === LICENSE_TEXT.USED ? usedLabel : unusedLabel}: `}
                  </span>
                  <span className="stat-text">
                    {entry.value} (~{((entry.value / totalValue) * 100).toFixed(2)}%)
                  </span>
                </Typography>
              </div>
            ))}
            <Typography className="total-label">
              {totalLabel}: <span className="stat-text">{totalValue}</span>
            </Typography>
          </div>

          {/* Chart */}
          <ResponsiveContainer width={140} height={140} data-testid="chart">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={65}
                dataKey="value"
              >
                {data.map((entry) => (<Cell key={entry.name} fill={entry.color} />))}
              </Pie>
              <RechartsToolTip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

LicenseCardComponent.propTypes = {
  title: PropTypes.string.isRequired,
  tooltipText: PropTypes.string.isRequired,
  usedLabel: PropTypes.string.isRequired,
  unusedLabel: PropTypes.string.isRequired,
  totalLabel: PropTypes.string.isRequired,
  dropdownOptions: PropTypes.array.isRequired,
};

export default LicenseCardComponent;